import React, { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from './ToastContainer';
import LoadingSpinner from './LoadingSpinner';
import ConfirmationModal from './ConfirmationModal';
import {
  useAgentSettings,
  useUpdateAgent,
  useDeleteAgent,
  useConnectCalendar,
  useServicesManagement,
  useApproveServices,
  useAddService,
  useDeleteService,
  useUpdateServiceStockStatus
} from '../hooks/useDashboard';
import type { AgentConfig } from '../hooks/useDashboard';

// Business types that have inventory/stock management
const INVENTORY_BUSINESS_TYPES = ['bakery', 'catering', 'cafe'];

// Helper function to format duration in user-friendly format
const formatDuration = (minutes: number): string => {
  if (!minutes) return '';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours === 0) {
    return `${mins} min`;
  } else if (mins === 0) {
    return hours === 1 ? '1 hr' : `${hours} hrs`;
  } else {
    const hrText = hours === 1 ? 'hr' : 'hrs';
    return `${hours} ${hrText} ${mins} min`;
  }
};

interface AgentSettingsProps {
  agentId: string;
  isOpen: boolean;
  onClose: () => void;
  onSave?: () => void;
}


const AgentSettings: React.FC<AgentSettingsProps> = ({ agentId, isOpen, onClose, onSave }) => {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('basic');
  const [agent, setAgent] = useState<AgentConfig | null>(null);
  const [originalAgent, setOriginalAgent] = useState<AgentConfig | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[tab: string]: string[]}>({});
  
  // Services management state
  const [newServiceName, setNewServiceName] = useState('');
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [serviceDurations, setServiceDurations] = useState<{[serviceName: string]: number}>({});

  // React Query hooks
  const { data: agentData, isLoading, error } = useAgentSettings(agentId, isOpen);
  const updateAgentMutation = useUpdateAgent();
  const deleteAgentMutation = useDeleteAgent();
  const connectCalendarMutation = useConnectCalendar();
  
  // Services management hooks - only load when services tab is active for better performance
  const { data: servicesData, isLoading: servicesLoading } = useServicesManagement(
    agentId, 
    isOpen && activeTab === 'services'
  );
  const approveServicesMutation = useApproveServices();
  const addServiceMutation = useAddService();
  const deleteServiceMutation = useDeleteService();
  const updateStockStatusMutation = useUpdateServiceStockStatus();

  // Derived state
  const isSaving = updateAgentMutation.isPending;
  const isDeleting = deleteAgentMutation.isPending;
  const isLoadingAny = isLoading || (activeTab === 'services' && servicesLoading);

  // Helper function to check if agent data has changed
  const hasUnsavedChanges = useCallback(() => {
    if (!agent || !originalAgent) return false;
    return JSON.stringify(agent) !== JSON.stringify(originalAgent);
  }, [agent, originalAgent]);

  // Helper function to get changes per tab
  const getTabChanges = () => {
    if (!agent || !originalAgent) return {};
    
    const changes: {[tab: string]: boolean} = {};
    
    // Basic tab changes (includes branding since it's in the same tab)
    changes.basic = (
      agent.businessName !== originalAgent.businessName ||
      agent.businessType !== originalAgent.businessType ||
      agent.businessDescription !== originalAgent.businessDescription ||
      agent.tone !== originalAgent.tone ||
      agent.faqsPolicies !== originalAgent.faqsPolicies ||
      JSON.stringify(agent.locationInfo) !== JSON.stringify(originalAgent.locationInfo) ||
      // Branding changes (logo, tagline, social links)
      agent.logoUrl !== originalAgent.logoUrl ||
      agent.tagline !== originalAgent.tagline ||
      JSON.stringify(agent.socialLinks) !== JSON.stringify(originalAgent.socialLinks)
    );

    // Behavior tab changes
    changes.behavior = (
      agent.settings?.responseLength !== originalAgent.settings?.responseLength ||
      agent.settings?.greetingMessage !== originalAgent.settings?.greetingMessage ||
      agent.settings?.fallbackResponse !== originalAgent.settings?.fallbackResponse
    );

    // Booking tab changes (includes operating hours since UI is in booking tab)
    changes.booking = (
      JSON.stringify(agent.settings?.booking) !== JSON.stringify(originalAgent.settings?.booking) ||
      JSON.stringify(agent.settings?.operatingHours) !== JSON.stringify(originalAgent.settings?.operatingHours) ||
      agent.calendarEnabled !== originalAgent.calendarEnabled
    );

    // Services tab - always false since it's managed separately
    changes.services = false;

    return changes;
  };

  // Validation function
  const validateTab = useCallback((tabId: string): string[] => {
    if (!agent) return [];
    const errors: string[] = [];
    
    switch (tabId) {
      case 'basic':
        if (!agent.businessName?.trim()) errors.push('Business name is required');
        if (!agent.businessType?.trim()) errors.push('Business type is required');
        if (!agent.businessDescription?.trim()) errors.push('Business description is required');
        break;
      case 'behavior':
        if (!agent.settings?.greetingMessage?.trim()) errors.push('Greeting message is required');
        if (!agent.settings?.fallbackResponse?.trim()) errors.push('Fallback response is required');
        break;
      case 'booking':
        if (agent.settings?.booking?.enabled && agent.settings.booking.defaultDuration < 15) {
          errors.push('Minimum appointment duration is 15 minutes');
        }
        break;
      case 'services':
        // No validation needed - services are managed separately
        break;
    }
    
    return errors;
  }, [agent]);

  // Auto-save to localStorage
  const autoSaveToLocalStorage = useCallback((agentData: AgentConfig) => {
    try {
      // Don't auto-save if we're in the middle of a calendar OAuth flow
      // (indicated by calendarEnabled being true but original being false)
      if (agentData.calendarEnabled && originalAgent && !originalAgent.calendarEnabled) {
        // Skip auto-save during OAuth to prevent conflicts
        return;
      }
      localStorage.setItem(`agent-draft-${agentId}`, JSON.stringify(agentData));
    } catch (error) {
      console.warn('Failed to auto-save to localStorage:', error);
    }
  }, [originalAgent, agentId]);

  // Load draft from localStorage
  const loadDraftFromLocalStorage = useCallback((): AgentConfig | null => {
    try {
      const draft = localStorage.getItem(`agent-draft-${agentId}`);
      return draft ? JSON.parse(draft) : null;
    } catch (error) {
      console.warn('Failed to load draft from localStorage:', error);
      return null;
    }
  }, [agentId]);

  // Clear draft from localStorage
  const clearDraftFromLocalStorage = useCallback(() => {
    try {
      localStorage.removeItem(`agent-draft-${agentId}`);
    } catch (error) {
      console.warn('Failed to clear draft from localStorage:', error);
    }
  }, [agentId]);

  // Update local state when React Query data arrives
  useEffect(() => {
    if (agentData?.agent) {
      // Check for draft first
      const draft = loadDraftFromLocalStorage();
      
      if (draft) {
        // Special handling for calendar OAuth - if the server agent has calendar enabled
        // but the draft has it disabled, it means OAuth was successful and we should
        // use the server state for calendar but keep other draft changes
        if (agentData.agent.calendarEnabled && !draft.calendarEnabled) {
          const updatedDraft = { ...draft, calendarEnabled: true };
          const restoreDraft = window.confirm(
            'You have unsaved changes from a previous session, and Google Calendar was successfully connected. Would you like to restore your other changes?'
          );
          
          if (restoreDraft) {
            setAgent(updatedDraft);
            setOriginalAgent(agentData.agent);
            showSuccess('Google Calendar connected successfully! Your other changes have been restored.');
            return;
          } else {
            clearDraftFromLocalStorage();
          }
        } else {
          // Normal draft restoration
          const restoreDraft = window.confirm(
            'You have unsaved changes from a previous session. Would you like to restore them?'
          );
          
          if (restoreDraft) {
            setAgent(draft);
            setOriginalAgent(agentData.agent);
            return;
          } else {
            clearDraftFromLocalStorage();
          }
        }
      }
      
      setAgent(agentData.agent);
      setOriginalAgent(agentData.agent);
    }
  }, [agentData, agentId, loadDraftFromLocalStorage, clearDraftFromLocalStorage, showSuccess]);

  // Auto-save changes to localStorage
  useEffect(() => {
    if (agent && originalAgent && hasUnsavedChanges()) {
      const timeoutId = setTimeout(() => {
        autoSaveToLocalStorage(agent);
      }, 1000); // Save after 1 second of no changes
      
      return () => clearTimeout(timeoutId);
    }
  }, [agent, originalAgent, hasUnsavedChanges, autoSaveToLocalStorage]);

  // Update validation errors when agent changes
  useEffect(() => {
    if (agent) {
      const tabIds = ['basic', 'behavior', 'booking', 'services'];
      const newErrors: {[tab: string]: string[]} = {};
      
      tabIds.forEach(tab => {
        const errors = validateTab(tab);
        if (errors.length > 0) {
          newErrors[tab] = errors;
        }
      });
      
      setValidationErrors(newErrors);
    }
  }, [agent, validateTab]);

  // Handle React Query error
  useEffect(() => {
    if (error) {
      showError('Failed to load agent settings');
    }
  }, [error, showError]);

  const saveSettings = async () => {
    if (!agent) return;

    // Validate all tabs before saving
    const allErrors: string[] = [];
    Object.keys(validationErrors).forEach(tab => {
      allErrors.push(...validationErrors[tab]);
    });

    if (allErrors.length > 0) {
      showError(`Please fix validation errors: ${allErrors.join(', ')}`);
      return;
    }

    updateAgentMutation.mutate({ agentId, agent }, {
      onSuccess: (data) => {
        showSuccess('Agent settings updated successfully');
        setOriginalAgent(agent); // Update original state
        clearDraftFromLocalStorage(); // Clear draft
        
        // Invalidate and refetch related queries to ensure data consistency
        queryClient.invalidateQueries({ queryKey: ['agent-settings', agentId] });
        if (activeTab === 'services') {
          queryClient.invalidateQueries({ queryKey: ['services-management', agentId] });
        }
        
        if (onSave) {
          onSave();
        }
      },
      onError: (error) => {
        showError(error.message || 'Failed to update agent settings');
      }
    });
  };

  const handleClose = () => {
    if (hasUnsavedChanges()) {
      setShowCloseConfirm(true);
    } else {
      clearDraftFromLocalStorage();
      onClose();
    }
  };

  const confirmClose = () => {
    clearDraftFromLocalStorage();
    setShowCloseConfirm(false);
    onClose();
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const deleteAgent = async () => {
    deleteAgentMutation.mutate(agentId, {
      onSuccess: () => {
        showSuccess('Agent deleted successfully');
        
        // Clear all related queries from cache
        queryClient.removeQueries({ queryKey: ['agent-settings', agentId] });
        queryClient.removeQueries({ queryKey: ['services-management', agentId] });
        queryClient.invalidateQueries({ queryKey: ['agents'] }); // Refresh agents list
        
        onClose();
        if (onSave) {
          onSave();
        }
        setShowDeleteConfirm(false);
      },
      onError: (error) => {
        showError(error.message || 'Failed to delete agent');
      }
    });
  };

  const updateAgent = (updates: Partial<AgentConfig>) => {
    if (agent) {
      setAgent({ ...agent, ...updates });
    }
  };

  const handleCalendarToggle = async (enabled: boolean) => {
    if (enabled && !agent?.calendarEnabled) {
      // User is enabling calendar integration - trigger OAuth flow
      try {
        const result = await connectCalendarMutation.mutateAsync(agentId);
        
        // Open OAuth popup
        const popup = window.open(
          result.authUrl,
          'google-calendar-auth',
          'width=500,height=600,scrollbars=yes,resizable=yes'
        );

        // Listen for postMessage from popup (better than polling)
        const handleMessage = (event: MessageEvent) => {
          // Verify origin for security (allow backend origin for OAuth callback)
          const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
          const allowedOrigins = [window.location.origin, backendUrl];
          
          console.log('Received message from origin:', event.origin, 'Data:', event.data);
          
          if (!allowedOrigins.includes(event.origin)) {
            console.log('Message rejected - origin not allowed:', event.origin);
            return;
          }
          
          if (event.data.type === 'CALENDAR_CONNECTED' && event.data.success) {
            console.log('Calendar connection success - updating UI');
            // Success - close popup and update state
            if (popup) popup.close();
            clearInterval(checkClosed);
            
            // Clear any drafts that might have the old calendar state
            clearDraftFromLocalStorage();
            
            // Update local state
            updateAgent({ calendarEnabled: true });
            showSuccess('Google Calendar connection completed. The connection will be saved automatically - you can continue editing other settings.');
          } else if (event.data.type === 'CALENDAR_ERROR') {
            console.log('Calendar connection error - showing error');
            // Error - close popup and show error
            if (popup) popup.close();
            clearInterval(checkClosed);
            showError('Failed to connect Google Calendar. Please try again.');
          }
        };
        
        window.addEventListener('message', handleMessage);
        
        // Fallback: Listen for the popup to close (in case postMessage fails)
        const checkClosed = setInterval(() => {
          if (popup?.closed) {
            clearInterval(checkClosed);
            window.removeEventListener('message', handleMessage);
            
            // Fallback success message if postMessage didn't work
            console.log('Popup closed - fallback success handling');
            // Clear any drafts that might have the old calendar state
            clearDraftFromLocalStorage();
            
            // Update local state and let user save
            updateAgent({ calendarEnabled: true });
            showSuccess('Google Calendar connection completed. The connection will be saved automatically - you can continue editing other settings.');
          }
        }, 1000);

        // Timeout after 5 minutes
        setTimeout(() => {
          if (popup && !popup.closed) {
            popup.close();
            clearInterval(checkClosed);
            window.removeEventListener('message', handleMessage);
            showError('Calendar authorization timed out. Please try again.');
          }
        }, 5 * 60 * 1000);

      } catch (error) {
        console.error('Calendar connection error:', error);
        showError('Failed to connect Google Calendar. Please try again.');
      }
    } else {
      // User is disabling calendar integration
      updateAgent({ calendarEnabled: enabled });
    }
  };

  const updateSettings = (settingsUpdates: Partial<AgentConfig['settings']>) => {
    if (agent) {
      setAgent({
        ...agent,
        settings: { 
          ...agent.settings, 
          ...settingsUpdates 
        }
      });
    }
  };

  const updateLocationInfo = (locationUpdates: Partial<AgentConfig['locationInfo']>) => {
    if (agent) {
      setAgent({
        ...agent,
        locationInfo: { ...agent.locationInfo, ...locationUpdates }
      });
    }
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateLocationInfo({ address: e.target.value });
  };


  const tabs = [
    { id: 'basic', name: 'Basic Info', icon: 'info' },
    { id: 'behavior', name: 'AI Behavior', icon: 'chat' },
    { id: 'booking', name: 'Booking', icon: 'calendar' },
    { id: 'services', name: 'Services', icon: 'services' }
  ];

  const getTabIcon = (icon: string) => {
    const iconClass = "w-5 h-5";
    switch (icon) {
      case 'info':
        return <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
      case 'chat':
        return <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>;
      case 'calendar':
        return <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>;
      case 'services':
        return <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>;
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-5xl h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Agent Settings
            </h2>
            {agent && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Configuring {agent.businessName}
              </p>
            )}
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {isLoadingAny ? (
          <div className="p-8 flex items-center justify-center">
            <LoadingSpinner size="lg" text={isLoading ? "Loading agent settings..." : "Loading..."} />
          </div>
        ) : agent ? (
          <div className="flex flex-1 overflow-hidden">
            {/* Sidebar */}
            <div className="w-64 bg-gray-50 dark:bg-gray-900 p-4 overflow-y-auto border-r border-gray-200 dark:border-gray-700">
              <nav className="space-y-1">
                {tabs.map((tab) => {
                  const tabChanges = getTabChanges();
                  const hasChanges = tabChanges[tab.id];
                  const hasErrors = validationErrors[tab.id]?.length > 0;
                  
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                        activeTab === tab.id
                          ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800 shadow-sm'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
                      }`}
                    >
                      <div className="flex items-center">
                        {getTabIcon(tab.icon)}
                        <span className="ml-3">{tab.name}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        {hasErrors && (
                          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" title="Has validation errors" />
                        )}
                        {hasChanges && (
                          <div className="w-2 h-2 bg-amber-500 rounded-full" title="Has unsaved changes" />
                        )}
                        {!hasErrors && !hasChanges && activeTab === tab.id && (
                          <div className="w-2 h-2 bg-green-500 rounded-full" title="All good" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Main Content */}
            <div className="flex-1 p-6 overflow-y-auto bg-white dark:bg-gray-800">
              {/* Basic Info Tab */}
              {activeTab === 'basic' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Basic Information</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Business Name
                        </label>
                        <input
                          type="text"
                          value={agent.businessName}
                          onChange={(e) => updateAgent({ businessName: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Business Type
                        </label>
                        <select
                          value={agent.businessType}
                          onChange={(e) => updateAgent({ businessType: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="salon">Salon</option>
                          <option value="spa">Spa</option>
                          <option value="hair salon">Hair Salon</option>
                          <option value="beauty salon">Beauty Salon</option>
                          <option value="nail salon">Nail Salon</option>
                          <option value="barbershop">Barbershop</option>
                          <option value="bakery">Bakery</option>
                          <option value="catering">Catering</option>
                          <option value="cafe">Cafe</option>
                          <option value="fitness studio">Fitness Studio</option>
                          <option value="gym">Gym</option>
                          <option value="yoga studio">Yoga Studio</option>
                          <option value="personal trainer">Personal Trainer</option>
                          <option value="auto service">Auto Service</option>
                          <option value="auto repair">Auto Repair</option>
                          <option value="car wash">Car Wash</option>
                          <option value="auto detailing">Auto Detailing</option>
                          <option value="mechanic">Mechanic</option>
                        </select>
                      </div>
                    </div>

                    <div className="mt-6">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Business Description
                      </label>
                      <textarea
                        value={agent.businessDescription}
                        onChange={(e) => updateAgent({ businessDescription: e.target.value })}
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Agent Tone
                        </label>
                        <select
                          value={agent.tone}
                          onChange={(e) => updateAgent({ tone: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="friendly">Friendly</option>
                          <option value="professional">Professional</option>
                          <option value="chill">Chill</option>
                          <option value="formal">Formal</option>
                          <option value="casual">Casual</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={agent.locationInfo?.phone || ''}
                          onChange={(e) => updateLocationInfo({ phone: e.target.value })}
                          placeholder="(555) 123-4567"
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div className="mt-6">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Address
                      </label>
                      <input
                        type="text"
                        value={
                          typeof agent.locationInfo?.address === 'string'
                            ? agent.locationInfo.address
                            : (agent.locationInfo?.address as any)?.formatted || ''
                        }
                        onChange={handleAddressChange}
                        placeholder="123 Main Street, City, State 12345"
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Enter your business address (street, city, state, zip code)
                      </p>
                    </div>

                    <div className="mt-6">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Business Information & Policies
                      </label>
                      <textarea
                        value={agent.faqsPolicies}
                        onChange={(e) => updateAgent({ faqsPolicies: e.target.value })}
                        rows={6}
                        placeholder="Add your business policies, menu items, FAQs, or other important information..."
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>

                    {agent.extractedDocuments && agent.extractedDocuments.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Uploaded Documents ({agent.extractedDocuments.length})
                        </label>
                        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                          <p className="text-sm text-green-800 dark:text-green-200">
                            {agent.extractedDocuments.length} document(s) have been processed and integrated into your agent's knowledge base.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Branding Section */}
                    <div className="pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
                      <h4 className="text-base font-medium text-gray-900 dark:text-white mb-3">Chat Page Branding</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Customize how your chat page looks to customers
                      </p>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Logo Upload */}
                        <ImageUpload
                          label=""
                          description="Square image, 500x500px recommended"
                          currentImage={agent.logoUrl}
                          onImageUpload={(url) => updateAgent({ logoUrl: url })}
                          onImageRemove={() => updateAgent({ logoUrl: '' })}
                          aspectRatio="square"
                        />
                      </div>

                      {/* Business Tagline */}
                      <div className="mt-6">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Business Tagline (Optional)
                        </label>
                        <input
                          type="text"
                          value={agent.tagline || ''}
                          onChange={(e) => updateAgent({ tagline: e.target.value })}
                          placeholder="e.g., Your neighborhood coffee spot"
                          maxLength={100}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Short description shown under your business type on the chat page (max 100 characters)
                        </p>
                      </div>

                      {/* Suggested Questions */}
                      <div className="mt-6">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Suggested Questions (Optional)
                        </label>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                          Add up to 4 suggested questions that appear when visitors first open the chat
                        </p>
                        <div className="space-y-2">
                          {[0, 1, 2, 3].map((index) => (
                            <input
                              key={index}
                              type="text"
                              value={(agent.suggestedQuestions && agent.suggestedQuestions[index]) || ''}
                              onChange={(e) => {
                                const newQuestions = [...(agent.suggestedQuestions || [])];
                                newQuestions[index] = e.target.value;
                                updateAgent({ suggestedQuestions: newQuestions.filter(q => q.trim() !== '') });
                              }}
                              placeholder={`Question ${index + 1} (e.g., "What services do you offer?")`}
                              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Social Links Section */}
                    <div className="pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
                      <h4 className="text-base font-medium text-gray-900 dark:text-white mb-3">Social Links</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Add social media links that will appear on your chat page (like Linktree).
                      </p>

                      {/* Existing Links */}
                      {agent.socialLinks && agent.socialLinks.length > 0 && (
                        <div className="space-y-3 mb-4">
                          {agent.socialLinks.map((link, index) => (
                            <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                                <select
                                  value={link.platform}
                                  onChange={(e) => {
                                    const newLinks = [...(agent.socialLinks || [])];
                                    newLinks[index] = { ...newLinks[index], platform: e.target.value };
                                    updateAgent({ socialLinks: newLinks });
                                  }}
                                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500"
                                >
                                  <option value="instagram">Instagram</option>
                                  <option value="facebook">Facebook</option>
                                  <option value="twitter">Twitter/X</option>
                                  <option value="tiktok">TikTok</option>
                                  <option value="linkedin">LinkedIn</option>
                                  <option value="youtube">YouTube</option>
                                  <option value="whatsapp">WhatsApp</option>
                                  <option value="website">Website</option>
                                  <option value="custom">Custom</option>
                                </select>
                                <input
                                  type="text"
                                  value={link.url}
                                  onChange={(e) => {
                                    const newLinks = [...(agent.socialLinks || [])];
                                    newLinks[index] = { ...newLinks[index], url: e.target.value };
                                    updateAgent({ socialLinks: newLinks });
                                  }}
                                  placeholder="https://..."
                                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500"
                                />
                                <input
                                  type="text"
                                  value={link.label || ''}
                                  onChange={(e) => {
                                    const newLinks = [...(agent.socialLinks || [])];
                                    newLinks[index] = { ...newLinks[index], label: e.target.value };
                                    updateAgent({ socialLinks: newLinks });
                                  }}
                                  placeholder="Label (optional)"
                                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500"
                                />
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newLinks = [...(agent.socialLinks || [])];
                                    newLinks[index] = { ...newLinks[index], isVisible: !link.isVisible };
                                    updateAgent({ socialLinks: newLinks });
                                  }}
                                  className={`p-2 rounded-lg transition-colors ${
                                    link.isVisible !== false
                                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                      : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
                                  }`}
                                  title={link.isVisible !== false ? 'Visible' : 'Hidden'}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    {link.isVisible !== false ? (
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    ) : (
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                    )}
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newLinks = (agent.socialLinks || []).filter((_, i) => i !== index);
                                    updateAgent({ socialLinks: newLinks });
                                  }}
                                  className="p-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                                  title="Remove"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add New Link Button */}
                      <button
                        type="button"
                        onClick={() => {
                          const newLinks = [...(agent.socialLinks || []), {
                            platform: 'instagram',
                            url: '',
                            label: '',
                            isVisible: true,
                            order: (agent.socialLinks || []).length
                          }];
                          updateAgent({ socialLinks: newLinks });
                        }}
                        className="w-full px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors flex items-center justify-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Social Link
                      </button>
                    </div>

                    {/* Danger Zone */}
                    <div className="pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
                      <h4 className="text-base font-medium text-red-600 dark:text-red-400 mb-3">Danger Zone</h4>
                      <div className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-red-800 dark:text-red-200">Delete this agent</p>
                          <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                            This action cannot be undone. All data will be permanently deleted.
                          </p>
                        </div>
                        <button
                          onClick={handleDeleteClick}
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}


              {/* Behavior Tab */}
              {activeTab === 'behavior' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Behavior Settings</h3>
                    
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Response Length
                        </label>
                        <select
                          value={agent.settings?.responseLength || 'medium'}
                          onChange={(e) => updateSettings({ responseLength: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="short">Short (1-2 sentences)</option>
                          <option value="medium">Medium (2-4 sentences)</option>
                          <option value="long">Long (Detailed responses)</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Greeting Message
                        </label>
                        <input
                          type="text"
                          value={agent.settings?.greetingMessage || ''}
                          onChange={(e) => updateSettings({ greetingMessage: e.target.value })}
                          placeholder="Hello! How can I help you today?"
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Fallback Response
                        </label>
                        <textarea
                          value={agent.settings?.fallbackResponse || ''}
                          onChange={(e) => updateSettings({ fallbackResponse: e.target.value })}
                          rows={3}
                          placeholder="I'm sorry, I don't have information about that. Please contact us directly for assistance."
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Booking Tab */}
              {activeTab === 'booking' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Booking Settings</h3>
                    
                    <div className="space-y-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-base font-medium text-gray-900 dark:text-white">Enable Booking</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-300">Allow customers to book appointments through the chat</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={agent.settings?.booking?.enabled || false}
                            onChange={(e) => updateSettings({
                              booking: { ...(agent.settings?.booking || {}), enabled: e.target.checked }
                            })}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                        </label>
                      </div>

                      {agent.settings?.booking?.enabled && servicesData && !servicesData.servicesReviewed && (
                        <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                          <div className="flex items-center">
                            <svg className="w-4 h-4 text-amber-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <span className="text-sm text-amber-800 dark:text-amber-200">Services need review for booking</span>
                          </div>
                          <button
                            onClick={() => setActiveTab('services')}
                            className="text-sm bg-amber-600 hover:bg-amber-700 text-white px-3 py-1 rounded transition-colors"
                          >
                            Review
                          </button>
                        </div>
                      )}

                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-base font-medium text-gray-900 dark:text-white">Calendar Integration</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-300">Connect with Google Calendar for automatic booking</p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={agent.calendarEnabled}
                              onChange={(e) => handleCalendarToggle(e.target.checked)}
                              className="sr-only peer"
                              disabled={connectCalendarMutation.isPending}
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
                          </label>
                        </div>
                        
                        {agent.calendarEnabled && (
                          <div className="flex items-center space-x-2 text-sm">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span className="text-green-600 dark:text-green-400">Google Calendar Connected</span>
                          </div>
                        )}
                        
                        {connectCalendarMutation.isPending && (
                          <div className="flex items-center space-x-2 text-sm">
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                            <span className="text-blue-600 dark:text-blue-400">Connecting to Google Calendar...</span>
                          </div>
                        )}
                      </div>

                      {/* Business Hours Configuration - Per Day Schedule */}
                      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6 space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-base font-medium text-gray-900 dark:text-white mb-2">Business Hours by Day</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                              Set different hours for each day of the week
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const defaultHours = agent.locationInfo?.businessHours?.start && agent.locationInfo?.businessHours?.end
                                ? { start: agent.locationInfo.businessHours.start, end: agent.locationInfo.businessHours.end }
                                : { start: '09:00', end: '17:00' };

                              const schedule = {
                                monday: { enabled: true, ...defaultHours },
                                tuesday: { enabled: true, ...defaultHours },
                                wednesday: { enabled: true, ...defaultHours },
                                thursday: { enabled: true, ...defaultHours },
                                friday: { enabled: true, ...defaultHours },
                                saturday: { enabled: false, start: '09:00', end: '17:00' },
                                sunday: { enabled: false, start: '09:00', end: '17:00' }
                              };

                              updateSettings({
                                operatingHours: {
                                  ...(agent.settings?.operatingHours || {}),
                                  enabled: true,
                                  schedule
                                }
                              });
                            }}
                            className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
                          >
                            Copy from Default Hours
                          </button>
                        </div>

                        <div className="space-y-3">
                          {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
                            const daySchedule = agent.settings?.operatingHours?.schedule?.[day] || { enabled: true, start: '09:00', end: '17:00' };
                            const dayLabel = day.charAt(0).toUpperCase() + day.slice(1);

                            return (
                              <div key={day} className="flex items-center space-x-3 bg-white dark:bg-gray-700 p-3 rounded-lg border border-gray-200 dark:border-gray-600">
                                <label className="flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={daySchedule.enabled}
                                    onChange={(e) => {
                                      const currentSchedule = agent.settings?.operatingHours?.schedule || {};
                                      updateSettings({
                                        operatingHours: {
                                          ...(agent.settings?.operatingHours || {}),
                                          enabled: true,
                                          schedule: {
                                            ...currentSchedule,
                                            [day]: { ...daySchedule, enabled: e.target.checked }
                                          }
                                        }
                                      });
                                    }}
                                    className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                  />
                                  <span className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300 w-24">{dayLabel}</span>
                                </label>

                                {daySchedule.enabled ? (
                                  <>
                                    <input
                                      type="time"
                                      value={daySchedule.start}
                                      onChange={(e) => {
                                        const currentSchedule = agent.settings?.operatingHours?.schedule || {};
                                        updateSettings({
                                          operatingHours: {
                                            ...(agent.settings?.operatingHours || {}),
                                            enabled: true,
                                            schedule: {
                                              ...currentSchedule,
                                              [day]: { ...daySchedule, start: e.target.value }
                                            }
                                          }
                                        });
                                      }}
                                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                                    />
                                    <span className="text-gray-500 dark:text-gray-400">to</span>
                                    <input
                                      type="time"
                                      value={daySchedule.end}
                                      onChange={(e) => {
                                        const currentSchedule = agent.settings?.operatingHours?.schedule || {};
                                        updateSettings({
                                          operatingHours: {
                                            ...(agent.settings?.operatingHours || {}),
                                            enabled: true,
                                            schedule: {
                                              ...currentSchedule,
                                              [day]: { ...daySchedule, end: e.target.value }
                                            }
                                          }
                                        });
                                      }}
                                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                                    />
                                  </>
                                ) : (
                                  <span className="text-sm text-gray-500 dark:text-gray-400 italic">Closed</span>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                          <div className="flex items-start">
                            <svg className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="text-sm text-blue-800 dark:text-blue-200">
                              <p className="text-xs">Customers can only book appointments during enabled hours. Uncheck a day to close for that day.</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="flex flex-col">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Default Appointment Duration (minutes)
                          </label>
                          <input
                            type="number"
                            value={agent.settings?.booking?.defaultDuration || 60}
                            onChange={(e) => updateSettings({
                              booking: { ...(agent.settings?.booking || {}), defaultDuration: parseInt(e.target.value) || 60 }
                            })}
                            min="15"
                            max="480"
                            step="15"
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                            Default length for appointments if not specified per service
                          </p>
                        </div>

                        <div className="flex flex-col">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Buffer Time Between Appointments (minutes)
                          </label>
                          <input
                            type="number"
                            value={agent.settings?.booking?.bufferTimeBetweenAppointments || 0}
                            onChange={(e) => updateSettings({
                              booking: { ...(agent.settings?.booking || {}), bufferTimeBetweenAppointments: parseInt(e.target.value) || 0 }
                            })}
                            min="0"
                            max="60"
                            step="5"
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                            Breathing room between appointments (0 = back-to-back)
                          </p>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Booking Confirmation Template
                        </label>
                        <textarea
                          value={agent.settings?.booking?.confirmationTemplate || ''}
                          onChange={(e) => updateSettings({
                            booking: { ...(agent.settings?.booking || {}), confirmationTemplate: e.target.value }
                          })}
                          rows={3}
                          placeholder="Your appointment has been confirmed for {date} at {time}."
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Use {'{date}'} and {'{time}'} placeholders for dynamic values
                        </p>
                      </div>

                      {/* Approval Mode Toggle */}
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                        <div>
                          <h4 className="text-base font-medium text-gray-900 dark:text-white">Require Approval for Bookings</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                            When enabled, bookings will be marked as pending until you manually approve them
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={agent.settings?.booking?.requiresApproval || false}
                            onChange={(e) => updateSettings({
                              booking: { ...(agent.settings?.booking || {}), requiresApproval: e.target.checked }
                            })}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                        </label>
                      </div>

                      {/* Break Times Section */}
                      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6 space-y-4">
                        <div>
                          <h4 className="text-base font-medium text-gray-900 dark:text-white mb-2">Break Times</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                            Block off time for lunch or breaks (applies every day)
                          </p>
                        </div>

                        {(agent.settings?.booking?.breaks?.length ?? 0) > 0 ? (
                          <div className="space-y-3">
                            {agent.settings.booking.breaks?.map((breakTime, index) => (
                              <div key={index} className="flex items-center space-x-3 bg-white dark:bg-gray-700 p-3 rounded-lg border border-gray-200 dark:border-gray-600">
                                <div className="flex items-center space-x-2 flex-1">
                                  <input
                                    type="time"
                                    value={breakTime.start || ''}
                                    onChange={(e) => {
                                      const updatedBreaks = [...(agent.settings?.booking?.breaks || [])];
                                      updatedBreaks[index] = { ...updatedBreaks[index], start: e.target.value };
                                      updateSettings({
                                        booking: {
                                          ...(agent.settings?.booking || {}),
                                          breaks: updatedBreaks
                                        }
                                      });
                                    }}
                                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                                  />
                                  <span className="text-gray-500 dark:text-gray-400">to</span>
                                  <input
                                    type="time"
                                    value={breakTime.end || ''}
                                    onChange={(e) => {
                                      const updatedBreaks = [...(agent.settings?.booking?.breaks || [])];
                                      updatedBreaks[index] = { ...updatedBreaks[index], end: e.target.value };
                                      updateSettings({
                                        booking: {
                                          ...(agent.settings?.booking || {}),
                                          breaks: updatedBreaks
                                        }
                                      });
                                    }}
                                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const updatedBreaks = agent.settings?.booking?.breaks?.filter((_, i) => i !== index) || [];
                                    updateSettings({
                                      booking: {
                                        ...(agent.settings?.booking || {}),
                                        breaks: updatedBreaks
                                      }
                                    });
                                  }}
                                  className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-1"
                                  title="Remove break"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                            No break times set. Click "Add Break" to add one.
                          </p>
                        )}

                        <button
                          type="button"
                          onClick={() => {
                            const currentBreaks = agent.settings?.booking?.breaks || [];
                            updateSettings({
                              booking: {
                                ...(agent.settings?.booking || {}),
                                breaks: [...currentBreaks, { start: '12:00', end: '13:00' }]
                              }
                            });
                          }}
                          className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
                        >
                          + Add Break
                        </button>
                      </div>

                      {/* Daily Booking Limits Section */}
                      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6 space-y-4">
                        <div>
                          <h4 className="text-base font-medium text-gray-900 dark:text-white mb-2">Daily Booking Limits</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                            Set daily limits to prevent overbooking and manage your capacity effectively.
                          </p>
                        </div>

                        {/* General Daily Limit */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              General Daily Limit
                            </label>
                            <div className="flex items-center space-x-2">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={agent.settings?.booking?.dailyLimit || ''}
                                onChange={(e) => {
                                  const value = e.target.value === '' ? null : parseInt(e.target.value);
                                  updateSettings({
                                    booking: { 
                                      ...(agent.settings?.booking || {}), 
                                      dailyLimit: value 
                                    }
                                  });
                                }}
                                placeholder="No limit"
                                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                              />
                              <span className="text-sm text-gray-500 dark:text-gray-400">per day</span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Maximum total bookings allowed per day (leave empty for no limit)
                            </p>
                          </div>
                        </div>

                        {/* Service-Specific Limits */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Service-Specific Limits
                            </label>
                            <button
                              type="button"
                              onClick={() => {
                                const currentLimits = agent.settings?.booking?.serviceSpecificLimits || [];
                                updateSettings({
                                  booking: {
                                    ...(agent.settings?.booking || {}),
                                    serviceSpecificLimits: [
                                      ...currentLimits,
                                      { serviceName: '', dailyLimit: 5 }
                                    ]
                                  }
                                });
                              }}
                              className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
                            >
                              + Add Service Limit
                            </button>
                          </div>
                          
                          {(agent.settings?.booking?.serviceSpecificLimits?.length ?? 0) > 0 ? (
                            <div className="space-y-3">
                              {agent.settings.booking.serviceSpecificLimits?.map((limit, index) => (
                                <div key={index} className="flex items-center space-x-3 bg-white dark:bg-gray-700 p-3 rounded-lg border border-gray-200 dark:border-gray-600">
                                  <div className="flex-1">
                                    <select
                                      value={limit.serviceName}
                                      onChange={(e) => {
                                        const updatedLimits = [...(agent.settings?.booking?.serviceSpecificLimits || [])];
                                        updatedLimits[index] = { ...updatedLimits[index], serviceName: e.target.value };
                                        updateSettings({
                                          booking: {
                                            ...(agent.settings?.booking || {}),
                                            serviceSpecificLimits: updatedLimits
                                          }
                                        });
                                      }}
                                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                                    >
                                      <option value="">Select a service...</option>
                                      {agent.approvedServices
                                        ?.filter((service) => {
                                          // Show current selection or services not already used in other limits
                                          const otherLimits = agent.settings?.booking?.serviceSpecificLimits?.filter((_, i) => i !== index) || [];
                                          return service.name === limit.serviceName || !otherLimits.some(l => l.serviceName === service.name);
                                        })
                                        .map((service, idx) => (
                                          <option key={idx} value={service.name}>
                                            {service.name}
                                          </option>
                                        ))}
                                    </select>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <input
                                      type="number"
                                      min="1"
                                      max="50"
                                      value={limit.dailyLimit}
                                      onChange={(e) => {
                                        const updatedLimits = [...(agent.settings?.booking?.serviceSpecificLimits || [])];
                                        updatedLimits[index] = { ...updatedLimits[index], dailyLimit: parseInt(e.target.value) || 1 };
                                        updateSettings({
                                          booking: {
                                            ...(agent.settings?.booking || {}),
                                            serviceSpecificLimits: updatedLimits
                                          }
                                        });
                                      }}
                                      className="w-16 px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                                    />
                                    <span className="text-xs text-gray-500 dark:text-gray-400">per day</span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const updatedLimits = agent.settings?.booking?.serviceSpecificLimits?.filter((_, i) => i !== index) || [];
                                      updateSettings({
                                        booking: {
                                          ...(agent.settings?.booking || {}),
                                          serviceSpecificLimits: updatedLimits
                                        }
                                      });
                                    }}
                                    className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-1"
                                    title="Remove limit"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                              No service-specific limits set. Click "Add Service Limit" to add one.
                            </p>
                          )}
                          
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            Set specific daily limits for individual services. These limits are checked in addition to the general daily limit.
                          </p>
                        </div>

                        {/* Current Status Preview */}
                        {(agent.settings?.booking?.dailyLimit || (agent.settings?.booking?.serviceSpecificLimits?.length ?? 0) > 0) && (
                          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                            <div className="flex items-center mb-2">
                              <svg className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <h5 className="text-sm font-medium text-blue-900 dark:text-blue-100">Daily Limits Active</h5>
                            </div>
                            <div className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                              {agent.settings.booking.dailyLimit && (
                                <div>• General limit: <span className="font-medium">{agent.settings.booking.dailyLimit}</span> bookings per day</div>
                              )}
                              {agent.settings.booking.serviceSpecificLimits?.map((limit, index) => (
                                limit.serviceName && (
                                  <div key={index}>
                                    • {limit.serviceName}: <span className="font-medium">{limit.dailyLimit}</span> bookings per day
                                  </div>
                                )
                              ))}
                            </div>
                            <p className="text-xs text-blue-700 dark:text-blue-300 mt-2">
                              (Your AI will automatically prevent overbooking when these limits are reached.)
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Services Tab */}
              {activeTab === 'services' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Services Management</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
                      Manage the services your AI extracts from conversations and approve them for use in analytics and booking.
                    </p>
                    
                    {servicesLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="text-center">
                          <LoadingSpinner size="md" />
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Loading services...</p>
                        </div>
                      </div>
                    ) : servicesData ? (
                      <div className="space-y-6">
                        {servicesData && !servicesData.servicesReviewed && (
                          <div className="flex items-center p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                            <svg className="w-4 h-4 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            <span className="text-sm text-blue-800 dark:text-blue-200">Review and approve AI-extracted services below</span>
                          </div>
                        )}

                        {/* AI Extracted Services */}
                        {servicesData && servicesData.aiExtractedServices && servicesData.aiExtractedServices.length > 0 && (
                          <div>
                            <div className="mb-3">
                              <h4 className="text-base font-medium text-gray-900 dark:text-white">
                                AI-Extracted Services ({servicesData.aiExtractedServices.length})
                              </h4>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Services auto-refresh when you update business info
                              </p>
                            </div>
                            
                            <div className="space-y-3 mb-4">
                              {servicesData.aiExtractedServices.map((service: any, index: number) => {
                                // Handle both string and object formats
                                const serviceName = typeof service === 'string' ? service : service.name;
                                const documentDuration = typeof service === 'object' ? service.documentDuration : null;
                                
                                return (
                                <div key={index} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                  <div className="flex items-center mb-2">
                                    <input
                                      type="checkbox"
                                      checked={selectedServices.includes(serviceName)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedServices([...selectedServices, serviceName]);
                                          // Auto-populate with document duration if available, otherwise use agent default
                                          if (!serviceDurations[serviceName]) {
                                            const autoPopulateDuration = documentDuration || agent?.settings?.booking?.defaultDuration || 60;
                                            setServiceDurations(prev => ({
                                              ...prev,
                                              [serviceName]: autoPopulateDuration
                                            }));
                                          }
                                        } else {
                                          setSelectedServices(selectedServices.filter(s => s !== serviceName));
                                          // Clear duration when unchecked
                                          setServiceDurations(prev => {
                                            const newDurations = { ...prev };
                                            delete newDurations[serviceName];
                                            return newDurations;
                                          });
                                        }
                                      }}
                                      className="mr-3 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                    />
                                    <div className="flex-grow">
                                      <span className="text-sm text-gray-900 dark:text-white capitalize">{serviceName}</span>
                                      {documentDuration && (
                                        <span className="ml-2 text-xs text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded">
                                          {formatDuration(documentDuration)} (from docs)
                                        </span>
                                      )}
                                    </div>
                                    <span className="text-xs text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded">
                                      AI-Extracted
                                    </span>
                                  </div>
                                  {selectedServices.includes(serviceName) && (
                                    <div className="ml-6 flex items-center space-x-2">
                                      <label className="text-xs text-gray-600 dark:text-gray-400">Duration:</label>
                                      <div className="flex items-center space-x-2">
                                        <input
                                          type="number"
                                          min="5"
                                          max="480"
                                          step="5"
                                          value={serviceDurations[serviceName] || documentDuration || agent?.settings?.booking?.defaultDuration || 60}
                                          onChange={(e) => {
                                            const value = parseInt(e.target.value);
                                            if (value >= 5 && value <= 480) {
                                              setServiceDurations(prev => ({
                                                ...prev,
                                                [serviceName]: value
                                              }));
                                            }
                                          }}
                                          className="w-16 text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        />
                                        <span className="text-xs text-gray-500 dark:text-gray-400">minutes</span>
                                        {(serviceDurations[serviceName] || documentDuration || agent?.settings?.booking?.defaultDuration) && (
                                          <span className="text-xs text-gray-500 dark:text-gray-400">
                                            ({formatDuration(serviceDurations[serviceName] || documentDuration || agent?.settings?.booking?.defaultDuration || 60)})
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                              })}
                            </div>

                            <div className="flex flex-wrap gap-2">
                              <button
                                onClick={() => {
                                  // Extract service names from the mixed array
                                  const serviceNames = servicesData.aiExtractedServices.map((service: any) => 
                                    typeof service === 'string' ? service : service.name
                                  );
                                  setSelectedServices(serviceNames);
                                }}
                                className="px-3 py-2 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                              >
                                Select All
                              </button>
                              <button
                                onClick={() => setSelectedServices([])}
                                className="px-3 py-2 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                              >
                                Deselect All
                              </button>
                              <button
                                onClick={() => {
                                  // Format services with durations
                                  const servicesWithDurations = selectedServices.map(serviceName => {
                                    // Find the original service data to get documentDuration
                                    let documentDuration: number | null = null;
                                    
                                    if (Array.isArray(servicesData.aiExtractedServices)) {
                                      const services = servicesData.aiExtractedServices as any[];
                                      const originalService = services.find((service: any) => 
                                        (typeof service === 'string' ? service : service.name) === serviceName
                                      );
                                      documentDuration = typeof originalService === 'object' ? originalService.documentDuration : null;
                                    }
                                    
                                    return {
                                      name: serviceName,
                                      // Only send manual duration if user actually changed it from the auto-populated value
                                      duration: serviceDurations[serviceName] !== (documentDuration || agent?.settings?.booking?.defaultDuration || 60) 
                                        ? serviceDurations[serviceName] 
                                        : undefined,
                                      documentDuration: documentDuration,
                                      isAIExtracted: true
                                    };
                                  });
                                  
                                  approveServicesMutation.mutate({ agentId, services: servicesWithDurations }, {
                                    onSuccess: () => {
                                      showSuccess(`${selectedServices.length} services approved successfully`);
                                      setSelectedServices([]);
                                      setServiceDurations({});
                                      // Refresh services data to update the UI immediately
                                      queryClient.invalidateQueries({ queryKey: ['services-management', agentId] });
                                    },
                                    onError: (error: any) => {
                                      showError(error.message || 'Failed to approve services');
                                    }
                                  });
                                }}
                                disabled={selectedServices.length === 0 || approveServicesMutation.isPending}
                                className="px-4 py-2 text-xs bg-primary-600 hover:bg-primary-700 text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {approveServicesMutation.isPending ? 'Approving...' : `Approve Selected (${selectedServices.length})`}
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Approved Services */}
                        <div>
                          <h4 className="text-base font-medium text-gray-900 dark:text-white mb-3">
                            Approved Services ({servicesData ? servicesData.approvedServices.length : 0})
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                            These services are active and will be used for analytics and booking detection.
                          </p>
                          
                          {!servicesData || servicesData.approvedServices.length === 0 ? (
                            <div className="text-center py-8 bg-gray-50 dark:bg-gray-700 rounded-lg">
                              <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                              </svg>
                              <p className="text-gray-500 dark:text-gray-400">No services approved yet</p>
                              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                Approve AI-extracted services or add services manually
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-2 mb-4">
                              {servicesData.approvedServices.map((service, index) => (
                                <div key={index} className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                                  <div className="flex flex-col">
                                    <span className="text-sm text-gray-900 dark:text-white capitalize">{service.name}</span>
                                    {(service.duration || service.documentDuration) && (
                                      <span className="text-xs text-gray-600 dark:text-gray-400">
                                        Duration: {formatDuration(service.duration || service.documentDuration || 60)}
                                        {service.duration && service.documentDuration && service.duration !== service.documentDuration && (
                                          <span className="text-green-600 dark:text-green-400 ml-1">(manual override)</span>
                                        )}
                                        {!service.duration && service.documentDuration && (
                                          <span className="text-green-600 dark:text-green-400 ml-1">(from docs)</span>
                                        )}
                                      </span>
                                    )}
                                    {INVENTORY_BUSINESS_TYPES.includes(agent?.businessType) && (
                                      <div className="flex items-center mt-1">
                                        <span className="text-xs text-gray-500 dark:text-gray-400 mr-2">Stock:</span>
                                        <select
                                          value={service.stockStatus || 'in_stock'}
                                          onChange={(e) => {
                                            updateStockStatusMutation.mutate({
                                              agentId,
                                              serviceIndex: index,
                                              stockStatus: e.target.value as 'in_stock' | 'low_stock' | 'out_of_stock'
                                            }, {
                                              onSuccess: () => {
                                                showSuccess('Stock status updated successfully');
                                              },
                                              onError: (error: any) => {
                                                showError(error.message || 'Failed to update stock status');
                                              }
                                            });
                                          }}
                                          className={`text-xs border rounded px-2 py-1 ${
                                            (service.stockStatus || 'in_stock') === 'in_stock' ? 'bg-green-50 border-green-300 text-green-700' :
                                            (service.stockStatus || 'in_stock') === 'low_stock' ? 'bg-yellow-50 border-yellow-300 text-yellow-700' :
                                            'bg-red-50 border-red-300 text-red-700'
                                          }`}
                                        >
                                          <option value="in_stock">In Stock</option>
                                          <option value="low_stock">Low Stock</option>
                                          <option value="out_of_stock">Out of Stock</option>
                                        </select>
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className={`text-xs px-2 py-1 rounded ${
                                      service.isAIExtracted
                                        ? 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30'
                                        : 'text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/30'
                                    }`}>
                                      {service.isAIExtracted ? 'AI-Extracted' : 'Manual'}
                                    </span>
                                    <button
                                    onClick={() => {
                                      deleteServiceMutation.mutate({ agentId, serviceIndex: index }, {
                                        onSuccess: () => {
                                          showSuccess('Service deleted successfully');
                                          // Invalidate services data to refresh the list
                                          queryClient.invalidateQueries({ queryKey: ['services-management', agentId] });
                                        },
                                        onError: (error: any) => {
                                          showError(error.message || 'Failed to delete service');
                                        }
                                      });
                                    }}
                                    disabled={deleteServiceMutation.isPending}
                                    className="text-red-500 hover:text-red-700 p-1 transition-colors"
                                  >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                      </button>
                                    </div>
                                  </div>
                              ))}
                            </div>
                          )}

                          {/* Add New Service */}
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={newServiceName}
                              onChange={(e) => setNewServiceName(e.target.value)}
                              placeholder="Add a new service manually..."
                              className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && newServiceName.trim()) {
                                  addServiceMutation.mutate({ agentId, serviceName: newServiceName.trim() }, {
                                    onSuccess: () => {
                                      showSuccess('Service added successfully');
                                      setNewServiceName('');
                                      // Refresh services data to update the UI immediately
                                      queryClient.invalidateQueries({ queryKey: ['services-management', agentId] });
                                    },
                                    onError: (error: any) => {
                                      showError(error.message || 'Failed to add service');
                                    }
                                  });
                                }
                              }}
                            />
                            <button
                              onClick={() => {
                                if (newServiceName.trim()) {
                                  addServiceMutation.mutate({ agentId, serviceName: newServiceName.trim() }, {
                                    onSuccess: () => {
                                      showSuccess('Service added successfully');
                                      setNewServiceName('');
                                      // Refresh services data to update the UI immediately
                                      queryClient.invalidateQueries({ queryKey: ['services-management', agentId] });
                                    },
                                    onError: (error: any) => {
                                      showError(error.message || 'Failed to add service');
                                    }
                                  });
                                }
                              }}
                              disabled={!newServiceName.trim() || addServiceMutation.isPending}
                              className="px-4 py-2 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {addServiceMutation.isPending ? 'Adding...' : 'Add'}
                            </button>
                          </div>
                        </div>

                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-gray-600 dark:text-gray-300">Failed to load services data</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>
          </div>
        ) : (
          <div className="p-8 text-center">
            <p className="text-gray-600 dark:text-gray-300">Failed to load agent settings</p>
          </div>
        )}

        {/* Footer */}
        {agent && (
          <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6">
                {hasUnsavedChanges() && (
                  <div className="flex items-center text-sm text-amber-600 dark:text-amber-400">
                    <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse mr-2"></div>
                    Unsaved changes
                  </div>
                )}
                {Object.keys(validationErrors).length > 0 && (
                  <div className="flex items-center text-sm text-red-600 dark:text-red-400">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse mr-2"></div>
                    {Object.values(validationErrors).flat().length} error(s)
                  </div>
                )}
                {!hasUnsavedChanges() && Object.keys(validationErrors).length === 0 && (
                  <div className="flex items-center text-sm text-green-600 dark:text-green-400">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    All changes saved
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleClose}
                  className="px-6 py-2.5 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={saveSettings}
                  disabled={isSaving || Object.keys(validationErrors).length > 0}
                  className="flex items-center px-6 py-2.5 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                >
                  {isSaving ? (
                    <>
                      <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={deleteAgent}
        title="Delete Agent"
        message="Are you sure you want to delete this agent? This action cannot be undone and will permanently delete the agent and all its data."
        confirmText="Delete Agent"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleting}
      />

      {/* Close Confirmation Modal */}
      <ConfirmationModal
        isOpen={showCloseConfirm}
        onClose={() => setShowCloseConfirm(false)}
        onConfirm={confirmClose}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to close without saving? Your changes will be lost."
        confirmText="Discard Changes"
        cancelText="Keep Editing"
        type="warning"
      />
    </div>
  );
};

// Image Upload Component
interface ImageUploadProps {
  label: string;
  description: string;
  currentImage?: string;
  onImageUpload: (url: string) => void;
  onImageRemove: () => void;
  aspectRatio: 'square' | 'wide';
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  label,
  description,
  currentImage,
  onImageUpload,
  onImageRemove,
  aspectRatio
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const { showSuccess, showError } = useToast();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image must be less than 5MB');
      return;
    }

    setUploadError(null);
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      // The backend returns a relative URL like /uploads/images/filename
      // We need to make it absolute with the API URL
      const fullUrl = `${process.env.REACT_APP_API_URL}${data.url}`;
      console.log('Uploaded image URL:', fullUrl); // Debug log
      onImageUpload(fullUrl);
      showSuccess('Image uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError('Failed to upload image. Please try again.');
      showError('Failed to upload image');
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const previewClass = aspectRatio === 'square'
    ? 'w-32 h-32 rounded-full object-cover'
    : 'w-full h-32 rounded-lg object-cover';

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {label}
        </label>
      )}

      {/* Current Image Preview */}
      {currentImage && (
        <div className="mb-3 relative inline-block">
          <img
            src={currentImage}
            alt={label}
            className={`${previewClass} border-2 border-gray-200 dark:border-gray-600`}
            onError={(e) => {
              console.error('Image failed to load:', currentImage);
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <button
            type="button"
            onClick={onImageRemove}
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
            title="Remove image"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Upload Button */}
      <div className="flex items-center gap-3">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          id={`${label}-upload`}
        />
        <label
          htmlFor={`${label}-upload`}
          className={`cursor-pointer inline-flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isUploading ? (
            <>
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Uploading...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              {currentImage ? 'Change Image' : 'Upload Image'}
            </>
          )}
        </label>
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
        {description} • Max 5MB • JPG, PNG, GIF, WebP
      </p>

      {uploadError && (
        <p className="text-xs text-red-600 dark:text-red-400 mt-1">
          {uploadError}
        </p>
      )}
    </div>
  );
};

export default AgentSettings;